import { ref, computed, watch } from "vue";
import { defineStore, storeToRefs } from "pinia";
import genToken from "@utils/generateToken";
import { getClientId } from "@utils/clientId";
import { deepCopy } from "@utils/toolBox";
import handleError from "@composables/handleError.js";
import { useSurveyStore } from "@stores/survey/survey";
import { useSurveyLocalesStore } from "@stores/survey/surveyLocales";
import { useSurveyQuestionsStore } from "@stores/surveyQuestions";
import { useQuotasStore } from "@stores/survey/quotas";

import useMyFetch from "./useMyFetch";
import { captureEvent } from "@/lib/analytics";

export const useWeightingsStore = defineStore("weightings", () => {
  const surveyStore = useSurveyStore();
  const { survey } = storeToRefs(surveyStore);

  const surveyQuestionStore = useSurveyQuestionsStore();
  const { currentQuestions } = storeToRefs(surveyQuestionStore);

  const quotasStore = useQuotasStore();
  const { quotas: entityQuotas } = storeToRefs(quotasStore);

  const { localizeText } = useSurveyLocalesStore();

  const weightings = ref([]);
  const selectedWeighting = ref(null);
  const selectedWeightingIndex = ref(0);
  const saveAttempted = ref(false);

  watch(selectedWeightingIndex, () => {
    saveAttempted.value = false;
  });

  const defaultWeighting = {
    token: "",
    targets: [],
    total: 0,
    saved: false,
    saving: false,
    launched: false,
    clearing: false,
    launchHasError: false,
    quotas: [],
    new: true,
  };
  const defaultQuota = {
    quota_unit: "number",
    question_id: null,
    question_name: null,
    answers: [],
  };

  async function fetchWeightings() {
    if (!survey.value) return [];

    await quotasStore.fetchQuotas();

    const { data } = await useMyFetch(`/surveys/${survey.value.token}/weightings`).json();
    weightings.value = data.value.weightings;

    if (weightings.value.length > 0) {
      selectedWeighting.value = weightings.value[0];
      selectedWeightingIndex.value = 0;
    }

    weightings.value.forEach((weighting) => {
      weighting.saved = true;
    });

    return weightings.value;
  }

  function addNewWeighting() {
    const newWeighting = {
      ...deepCopy(defaultWeighting),
      name: `Weighting ${weightings.value.length + 1}`,
      token: genToken(),
    };
    weightings.value.push(newWeighting);
    selectedWeightingIndex.value = weightings.value.length - 1;
    selectedWeighting.value = weightings.value[selectedWeightingIndex.value];
  }

  function addQuotas() {
    const newQuota = deepCopy(defaultQuota);
    selectedWeighting.value.quotas.push(newQuota);
  }

  const defaultQuotasEnabled = computed(() => {
    if (selectedWeighting.value.quotas.length > 0) return false;
    if (selectedWeighting.value.targets.length > 1) return false;
    const targetId = selectedWeighting.value.targets[0];
    return entityQuotas.value.some((q) => q.survey_target_id === targetId);
  });

  function generateDefaultQuotas() {
    const targetId = selectedWeighting.value.targets[0];
    const targetQuotas = entityQuotas.value.filter((q) => q.survey_target_id === targetId);

    targetQuotas.forEach((quota) => {
      const question = findQuestion(quota.card_token);

      if (question && ["single", "rating"].includes(question.question_type)) {
        selectedWeighting.value.quotas.push({
          quota_unit: quota.unit,
          question_id: question.id,
          question_name: question.name,
          answers: quota.answer_quotas.map((aq) => ({
            code: aq.answer,
            name: question.choices[aq.answer].title,
            target: aq.target_value,
          })),
        });
      }
    });
  }

  const findQuestion = (cardToken) => {
    const question = currentQuestions.value.find((q) => q.card_token === cardToken);
    return question;
  };

  function removeQuota(index) {
    selectedWeighting.value.quotas.splice(index, 1);
  }

  function saveWeighting() {
    saveAttempted.value = false;
    selectedWeighting.value.saved = true;
    weightings.value[selectedWeightingIndex.value] = selectedWeighting.value;
    if (selectedWeighting.value.new) {
      fetchCreateWeighting();
      selectedWeighting.value.new = false;
    } else {
      fetchSaveWeighting();
    }
  }

  async function deleteWeighting(idx) {
    const deletedWeighting = weightings.value.find((weighting, _idx) => _idx === idx);
    if (!deletedWeighting.new) {
      const { response } = await useMyFetch(`/surveys/${survey.value.token}/weightings/${deletedWeighting.token}`)
        .delete({ client_id: getClientId() })
        .json();

      if (response.value.ok) captureEvent("weighting_deleted");
    }
    weightings.value.splice(idx, 1);
    if (!weightings.value) weightings.value = [];
    if (weightings.value.length > 0) {
      selectedWeighting.value = weightings.value[0];
      selectedWeightingIndex.value = 0;
    } else {
      selectedWeighting.value = null;
    }
  }

  async function fetchSaveWeighting() {
    const index = selectedWeightingIndex.value;
    weightings.value[index].saving = true;
    try {
      const url = `/surveys/${survey.value.token}/weightings/${selectedWeighting.value.token}`;
      const { response } = await useMyFetch(url).put(weightingBody.value).json();

      if (response.value.ok) captureEvent("weighting_saved");
    } catch (err) {
      handleError(err);
      weightings.value[index].saving = false;
    }
  }

  async function fetchCreateWeighting() {
    const index = selectedWeightingIndex.value;
    weightings.value[index].saving = true;
    const { data, response } = await useMyFetch(`/surveys/${survey.value.token}/weightings`)
      .post(weightingBody.value)
      .json();
    selectedWeighting.value.token = data.value.token;

    if (response.value.ok) captureEvent("weighting_created");
    weightings.value[index].saving = false;
  }

  async function launch() {
    const index = selectedWeightingIndex.value;
    weightings.value[index].launched = true;
    weightings.value[index].launchHasError = false;
    try {
      const url = `/surveys/${survey.value.token}/weightings/${weightings.value[index].token}/launch`;
      const { response } = await useMyFetch(url).post({ client_id: getClientId() }).json();

      if (response.value.ok) captureEvent("weighting_launched");
    } catch (err) {
      handleError(err);
      weightings.value[index].launchHasError = true;
      weightings.value[index].launched = false;
    }
  }

  async function clear() {
    const index = selectedWeightingIndex.value;
    weightings.value[index].clearing = true;
    weightings.value[index].launchHasError = false;

    const url = `/surveys/${survey.value.token}/weightings/${weightings.value[index].token}/clear`;
    try {
      const { response } = await useMyFetch(url).post({ client_id: getClientId() }).json();

      if (response.value.ok) captureEvent("weighting_cleared");
    } catch (err) {
      handleError(err);
      weightings.value[index].clearing = false;
    }
  }

  const updateAnswersQuotas = (questionId, index) => {
    const question = currentQuestions.value.find((q) => q.id === questionId);

    selectedWeighting.value.quotas[index].question_id = question.id;
    selectedWeighting.value.quotas[index].question_name = question.name;

    const answers = Object.values(question.choices);
    selectedWeighting.value.quotas[index].answers = answers.map((answer) => ({
      code: answer.code,
      name: localizeText(answer.localized_title),
      target: 0,
    }));
  };

  const quotaQuestionOptions = computed(() => {
    const options = currentQuestions.value.filter((e) => ["single", "rating"].includes(e.question_type));
    return options.map((q) => ({ value: q.id, label: q.name }));
  });

  const clearWeightingStats = (index) => {
    weightings.value[index].min_weight = null;
    weightings.value[index].max_weight = null;
    weightings.value[index].quality = null;
    weightings.value[index].average_weight = null;
    weightings.value[index].launched_at = null;
    weightings.value[index].quotas.forEach((quota) => quota.answers.forEach((answer) => (answer.avg_weight = null)));
  };

  const updateWeightingStats = (index, data) => {
    const changedWeighting = weightings.value[index];
    changedWeighting.min_weight = data.min_weight;
    changedWeighting.max_weight = data.max_weight;
    changedWeighting.quality = data.quality;
    changedWeighting.average_weight = data.average_weight;
    changedWeighting.launched_at = data.launched_at;
    data.quotas?.forEach((quota, idx) => {
      quota.answers.forEach((answer, idx2) => {
        changedWeighting.quotas[idx].answers[idx2] = answer;
      });
    });
  };

  const quotaUnitOptions = [
    { label: "Number", value: "number" },
    { label: "Percentage", value: "percentage" },
  ];

  const isTargetSelected = (targetId) => {
    const existingWeightingTargets = weightings.value
      .filter((w) => w.token !== selectedWeighting.value.token)
      .flatMap((weighting) => weighting.targets);
    return existingWeightingTargets.includes(targetId) && !selectedWeighting.value.targets.includes(targetId);
  };

  const hasDuplicatedTargets = computed(() => {
    const existingWeightingTargets = weightings.value
      .filter((w) => w.token !== selectedWeighting.value.token)
      .flatMap((weighting) => weighting.targets);
    return existingWeightingTargets.filter((target) => selectedWeighting.value.targets.includes(target)).length > 0;
  });

  const weightingBody = computed(() => {
    return {
      name: selectedWeighting.value.name,
      token: selectedWeighting.value.token,
      total: selectedWeighting.value.total,
      targets: selectedWeighting.value.targets,
      quotas: selectedWeighting.value.quotas,
    };
  });

  // weights channel handlers
  const weightsChannelHandlers = (cable, surveyToken) => {
    return {
      connected: subscribeToChannel(cable, surveyToken),
      rejected: () => console.log("rejected"),
      received: handleReceivedMessage,
    };
  };

  const subscribeToChannel = (cable, surveyToken) => () =>
    cable.perform({
      channel: "WeightsChannel",
      action: "subscribe",
      data: { survey_token: surveyToken },
    });

  const handleReceivedMessage = (msg) => {
    const index = weightings.value.findIndex((w) => w.token === msg.token);
    if (index !== -1) {
      switch (msg.action) {
        case "compute":
          if (msg.error) weightings.value[index].launchHasError = true;
          else updateWeightingStats(index, msg);
          weightings.value[index].launched = false;

          break;
        case "clear":
          clearWeightingStats(index);
          weightings.value[index].clearing = false;
          weightings.value[index].saving = false;
          break;
      }
    }
  };

  const weightingLaunchEnabled = computed(() => {
    if (["complete", "paused"].includes(survey.value.status)) return true;
    return false;
  });

  return {
    // state
    weightings,
    selectedWeighting,
    selectedWeightingIndex,
    saveAttempted,
    defaultQuota,
    // actions
    fetchWeightings,
    addNewWeighting,
    addQuotas,
    defaultQuotasEnabled,
    generateDefaultQuotas,
    saveWeighting,
    removeQuota,
    deleteWeighting,
    updateAnswersQuotas,
    launch,
    clear,
    isTargetSelected,
    hasDuplicatedTargets,
    // computed
    quotaQuestionOptions,
    quotaUnitOptions,
    // weights channel
    weightsChannelHandlers,
    weightingLaunchEnabled,
  };
});
